import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildProductIndex,
  filterIndexByScope,
  hydrateProducts,
  readPackQtdsComPreco,
  readProductByCode,
  readRestricoesDoCliente,
  readUltimaCompraCodigos,
  sortIndex,
  type ProductIndexEntry,
  type ProductScope,
  type ProductSortMode,
} from '@/shared/bfSeller';
import { useCart } from '@/hooks';
import type { ProductProps } from '@/types';
import {
  getCachedProduct,
  markProductMisses,
  putProductsInDictionary,
  resetProductDictionaryForScope,
} from './productDictionaryStore';
import { productScopeSignature, useProductScope } from './useProductDictionary';

/**
 * Catálogo sob demanda para as listas de produto.
 *
 * O índice leve mora aqui e é a única coisa que fica em memória: código, descrição, marca,
 * categoria, fabricante, embalagens compactadas e os escopos em que o produto existe. Filtro,
 * busca e ordenação rodam sobre ele; só a página visível é aberta do IndexedDB, com preço.
 *
 * O que saiu do caminho foi o `dicionario_produtos` inteiro — antes as três telas dependiam de
 * ter os 3,5 mil produtos montados e precificados em memória para mostrar os quatro primeiros.
 */

type ScopeKey = string;

/**
 * Cache de índice por escopo, fora do React: as três telas montam e desmontam o tempo todo, e
 * remontar o índice a cada navegação anularia o ganho. Invalidado quando o sync grava dados novos.
 */
const indexCache = new Map<ScopeKey, ProductIndexEntry[]>();
const indexInFlight = new Map<ScopeKey, Promise<ProductIndexEntry[]>>();
let indexGeneration = 0;

/** Chamar após um sync — o catálogo no banco mudou. */
export const invalidateProductIndex = (): void => {
  indexCache.clear();
  indexInFlight.clear();
  indexGeneration += 1;
};

const scopeKeyOf = (scope: ProductScope): ScopeKey =>
  `${Number(scope.codCliente) || 0}:${Number(scope.codLoja) || 0}:${(scope.segmentos || []).map(Number).sort().join(',')}`;

const loadIndexForScope = async (scope: ProductScope): Promise<ProductIndexEntry[]> => {
  const key = scopeKeyOf(scope);

  const cached = indexCache.get(key);
  if (cached) return cached;

  const inFlight = indexInFlight.get(key);
  if (inFlight) return inFlight;

  const generationAtStart = indexGeneration;
  const promise = (async () => {
    const [full, restritos, qtdsComPreco] = await Promise.all([
      buildProductIndex(),
      readRestricoesDoCliente(scope.codCliente),
      readPackQtdsComPreco(scope),
    ]);
    const scoped = filterIndexByScope(full, scope).filter((entry) => {
      if (restritos.has(entry.cod)) return false;
      if (qtdsComPreco.size > 0 && !qtdsComPreco.has(entry.cod)) return false;
      return true;
    });
    if (generationAtStart === indexGeneration) indexCache.set(key, scoped);
    return scoped;
  })().finally(() => {
    indexInFlight.delete(key);
  });

  indexInFlight.set(key, promise);
  return promise;
};

export type AvailabilityChecker = (entry: ProductIndexEntry) => boolean;

export const useProductCatalog = () => {
  const { getLimit } = useCart();

  const [index, setIndex] = useState<ProductIndexEntry[]>([]);
  const [isLoadingIndex, setIsLoadingIndex] = useState(true);
  const [packQtdsComPreco, setPackQtdsComPreco] = useState<Map<number, Set<number>> | null>(null);
  const packsRequestedRef = useRef('');

  /** Mesmo escopo do dicionário compartilhado — os dois precisam invalidar juntos. */
  const scope = useProductScope();
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const scopeKey = productScopeSignature(scope);

  useEffect(() => {
    resetProductDictionaryForScope(scopeKey);

    if (!scope.codLoja) {
      setIndex([]);
      setIsLoadingIndex(false);
      return;
    }

    let cancelled = false;
    setIsLoadingIndex(true);

    loadIndexForScope(scope)
      .then((entries) => {
        if (!cancelled) setIndex(entries);
      })
      .catch(() => {
        if (!cancelled) setIndex([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingIndex(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scopeKey]);

  /**
   * O mapa de embalagens com preço só é lido quando alguém pergunta por disponibilidade — é o
   * único filtro que precisa saber de preço, e não faz sentido pagar por ele nas telas onde
   * ninguém marcou esse filtro.
   */
  const ensureAvailabilityData = useCallback(() => {
    if (!scope.codLoja || packsRequestedRef.current === scopeKey) return;
    packsRequestedRef.current = scopeKey;
    readPackQtdsComPreco(scope)
      .then(setPackQtdsComPreco)
      .catch(() => setPackQtdsComPreco(new Map()));
  }, [scopeKey, scope.codLoja]);

  useEffect(() => {
    packsRequestedRef.current = '';
    setPackQtdsComPreco(null);
  }, [scopeKey]);

  /**
   * Mesma decisão do `isProductAvailable` antigo, só que sobre o índice: para alguma embalagem,
   * o estoque disponível cobre a quantidade exigida e existe preço para aquele pack no escopo.
   */
  const isAvailable = useCallback<AvailabilityChecker>(
    (entry) => {
      if (!entry?.packs?.length) return false;
      const disponivel = getLimit(entry.cod);
      if (disponivel <= 0) return false;

      const qtdsComPreco = packQtdsComPreco?.get(entry.cod);
      if (!qtdsComPreco?.size) return false;

      return entry.packs.some((pack) => {
        const exigido = pack.s === 'PK' && pack.m > 1 ? pack.m : pack.q;
        if (!exigido || disponivel < exigido) return false;
        return qtdsComPreco.has(pack.q);
      });
    },
    [getLimit, packQtdsComPreco]
  );

  const sorted = useCallback((entries: ProductIndexEntry[], mode: ProductSortMode) => sortIndex(entries, mode), []);

  /**
   * Abre os produtos de uma fatia do índice — mas só os que ainda não estão no cache
   * compartilhado. Produto já montado e precificado numa tela é reaproveitado nas outras: voltar
   * de `/categoria` para `/produtos` não reabre nada do banco nem recalcula preço.
   */
  const hydrate = useCallback(
    async (entries: ProductIndexEntry[]): Promise<ProductProps[]> => {
      const keyAtStart = scopeKey;
      const faltando = entries.filter((entry) => !getCachedProduct(Number(entry.cod)));

      if (faltando.length) {
        const novos = await hydrateProducts(faltando, scopeRef.current);
        if (productScopeSignature(scopeRef.current) !== keyAtStart) {
          return entries.map((entry) => getCachedProduct(Number(entry.cod))).filter(Boolean) as ProductProps[];
        }
        const devolvidos = new Set(novos.map((produto) => Number(produto.COD_PRODUTO)));
        putProductsInDictionary(novos);
        markProductMisses(faltando.map((entry) => Number(entry.cod)).filter((cod) => !devolvidos.has(cod)));
      }

      return entries.map((entry) => getCachedProduct(Number(entry.cod))).filter(Boolean) as ProductProps[];
    },
    [scopeKey]
  );

  const getByCode = useCallback(
    async (codProduto: number): Promise<ProductProps | null> => {
      const cached = getCachedProduct(Number(codProduto));
      if (cached) return cached;

      const produto = await readProductByCode(codProduto, scopeRef.current);
      if (produto) putProductsInDictionary([produto]);
      return produto;
    },
    [scopeKey]
  );

  /** Abre um conjunto pequeno e conhecido de produtos (recomendados, últimas compras, combos). */
  const hydrateByCodes = useCallback(
    async (codigos: number[]): Promise<ProductProps[]> => {
      if (!codigos.length || !index.length) return [];
      const wanted = new Set(codigos.map(Number));
      return hydrate(index.filter((entry) => wanted.has(entry.cod)));
    },
    [index, hydrate]
  );

  /** Códigos que o cliente já comprou — base da vitrine de últimas compras, sem varrer catálogo. */
  const getUltimaCompraCodigos = useCallback(
    () => readUltimaCompraCodigos(scope.codCliente),
    [scope.codCliente]
  );

  return {
    /** Índice leve do escopo ativo — base de filtro, busca e ordenação. */
    index,
    isLoadingIndex,
    scope,
    scopeKey,
    sorted,
    hydrate,
    hydrateByCodes,
    getByCode,
    getUltimaCompraCodigos,
    isAvailable,
    ensureAvailabilityData,
    hasAvailabilityData: packQtdsComPreco != null,
  };
};
